import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * User administration: who may use the Clinical Editor, and what they may do.
 *
 * Aliases into the generated contract, the `hooks.ts` rule: change the
 * contract, regenerate, and these names either still resolve or fail to
 * compile. Nothing here decides authority — `/v1/session` and the service's
 * per-request resolver do — and nothing here reads a capability off a role:
 * what a role MEANS arrives in `RoleCatalog`, from the server's own matrix.
 */
export type Role = components["schemas"]["Role"];
export type RoleAssignment = components["schemas"]["RoleAssignment"];
export type RoleAssignmentList = components["schemas"]["RoleAssignmentList"];
export type SubjectRoleSetRequest =
  components["schemas"]["SubjectRoleSetRequest"];
export type SubjectRoleSet = components["schemas"]["SubjectRoleSet"];
export type RoleCatalog = components["schemas"]["RoleCatalog"];
export type RoleCatalogEntry = components["schemas"]["RoleCatalogEntry"];
export type DirectoryUser = components["schemas"]["DirectoryUser"];
export type DirectoryUserPage = components["schemas"]["DirectoryUserPage"];
export type DirectoryResolution = components["schemas"]["DirectoryResolution"];
export type DirectoryResolvedUser =
  components["schemas"]["DirectoryResolvedUser"];
export type DirectorySource = components["schemas"]["DirectorySource"];

/** The service refuses anything shorter; the picker does not ask. */
export const DIRECTORY_MIN_QUERY_LENGTH = 2;
/** One page of people. The picker pages with the cursor; it never lists. */
export const DIRECTORY_PAGE_SIZE = 10;
/** The most subjects one resolve call may name — the contract's ceiling. */
export const DIRECTORY_RESOLVE_BATCH = 200;

/**
 * Every grant in this environment, revoked ones included.
 *
 * ONE read with `includeRevoked=true` rather than two toggled reads: the
 * access table groups live rows per person, the history view lists the
 * revoked ones, and "last changed" needs both. A grants table holds
 * (people × roles) rows, so the whole thing is one small response and the
 * history toggle is a client-side view of it.
 */
export function useRoleAssignmentsQuery(): UseQueryResult<RoleAssignmentList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.roleAssignments,
    async queryFn() {
      return unwrap(
        await client.GET("/v1/role-assignments", {
          params: { query: { includeRevoked: true } },
        }),
      );
    },
  });
}

/**
 * The role vocabulary as the deployment's matrix defines it.
 *
 * Policy, read from the server: which capabilities a role carries and which
 * roles it already includes. The browser renders summaries and refuses
 * redundant selections FROM this document, so a matrix amendment reaches the
 * screen by deployment rather than by a second edit here. Cached like the
 * registries it resembles — it moves with a rollout, not with a click.
 */
export function useRoleCatalogQuery(enabled = true): UseQueryResult<RoleCatalog> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.roleCatalog,
    enabled,
    staleTime: 5 * 60_000,
    async queryFn() {
      return unwrap(await client.GET("/v1/role-catalog"));
    },
  });
}

/**
 * A directory search, paged by the server's cursor.
 *
 * Disabled below the minimum length so a single character never reaches the
 * service. Previous data remains cached while the next answer arrives, but
 * the picker hides placeholder results so they cannot be selected for a new
 * query. The query is the TRIMMED text: two spellings that differ only in
 * surrounding whitespace share one cache entry.
 */
export function useDirectorySearchQuery(
  query: string,
): UseInfiniteQueryResult<InfiniteData<DirectoryUserPage>> {
  const client = useApiClient();
  const trimmed = query.trim();
  return useInfiniteQuery({
    queryKey: queryKeys.directorySearch(trimmed),
    enabled: trimmed.length >= DIRECTORY_MIN_QUERY_LENGTH,
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      return unwrap(
        await client.POST("/v1/directory/users", {
          body: {
            query: trimmed,
            limit: DIRECTORY_PAGE_SIZE,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      );
    },
    getNextPageParam: (last) => last.nextCursor ?? null,
    placeholderData: keepPreviousData,
  });
}

/**
 * The people behind a set of subjects — the table's display names and emails.
 *
 * Keyed by the sorted, de-duplicated list so the row set decides the cache
 * entry, not render order. Batched at the contract's ceiling and merged, so a
 * deployment with more grants than one call admits still resolves every row.
 * A subject the directory does not know comes back with `user: null`; the
 * caller renders the object id in its place and never invents a name.
 */
export function useDirectoryResolveQuery(
  subjects: readonly string[],
): UseQueryResult<DirectoryResolution> {
  const client = useApiClient();
  const distinct = [...new Set(subjects)].sort();
  return useQuery({
    queryKey: queryKeys.directoryResolve(distinct),
    enabled: distinct.length > 0,
    async queryFn() {
      const batches: string[][] = [];
      for (let i = 0; i < distinct.length; i += DIRECTORY_RESOLVE_BATCH) {
        batches.push(distinct.slice(i, i + DIRECTORY_RESOLVE_BATCH));
      }
      const pages = await Promise.all(
        batches.map(async (batch) =>
          unwrap(
            await client.POST("/v1/directory/users/resolve", {
              body: { subjects: batch },
            }),
          ),
        ),
      );
      const first = pages[0];
      if (first === undefined) {
        // Unreachable: `enabled` requires at least one subject.
        throw new Error("directory resolution ran with no subjects");
      }
      return {
        source: first.source,
        items: pages.flatMap((page) => page.items),
      };
    },
  });
}

/**
 * Replace one person's whole live role set atomically.
 *
 * The ONLY write this surface performs. Grant, edit and remove are all this
 * request with a different `roles` list, and every one of them carries the
 * live assignment ids the administrator was looking at — the service's
 * compare-and-set, so two administrators cannot both "win" and a grant
 * revoked meanwhile is a refusal rather than a silent re-grant.
 *
 * `onSuccess` AWAITS the invalidation of the whole access root, so the dialog
 * that called `mutateAsync` closes over a table that has already re-read the
 * service's answer rather than over its own optimistic belief. The session is
 * re-read too when the actor edited THEMSELVES: their own capabilities may
 * have changed, and the shell's controls must follow the server.
 *
 * `onError` invalidates on a stale refusal and on an ambiguous outcome — in
 * both, the truthful state is whatever the service holds now, and the page
 * must show that rather than the request that was refused.
 */
export function useReplaceSubjectRolesMutation({
  selfSubject,
}: {
  selfSubject: string;
}): UseMutationResult<SubjectRoleSet, unknown, SubjectRoleSetRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(body: SubjectRoleSetRequest) {
      return unwrap(await client.PUT("/v1/subject-roles", { body }));
    },
    async onSuccess(_result, variables) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accessRoot });
      if (variables.subject === selfSubject) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      }
    },
    async onError(error) {
      const failure = classifyAccessFailure(error);
      if (failure.kind === "stale" || failure.kind === "ambiguous") {
        await queryClient.invalidateQueries({ queryKey: queryKeys.accessRoot });
      }
    },
    retry: false,
  });
}

/**
 * Why a role-set write was refused, as kinds the dialogs key their copy on.
 *
 * `stale` carries the live assignment ids the service reported, read from the
 * envelope with an `undefined`-tolerant guard because the envelope is data.
 * `self-admin` is the service's own sentence: it is the one refusal whose
 * wording the server owns and the client repeats.
 */
export type AccessFailure =
  | { kind: "stale"; currentAssignmentIds: string[] | undefined }
  | { kind: "self-admin"; message: string }
  | { kind: "redundant"; message: string }
  | { kind: "denied" }
  | { kind: "unauthenticated" }
  | { kind: "directory-unavailable" }
  | { kind: "unavailable" }
  | { kind: "ambiguous"; code: string; requestId: string | undefined }
  | {
      kind: "error";
      code: string;
      requestId: string | undefined;
      message: string;
    };

export function classifyAccessFailure(error: unknown): AccessFailure {
  if (error instanceof ApiError) {
    if (error.code === "role_set_stale") {
      const ids = error.envelope["currentAssignmentIds"];
      return {
        kind: "stale",
        currentAssignmentIds:
          Array.isArray(ids) && ids.every((id) => typeof id === "string")
            ? (ids as string[])
            : undefined,
      };
    }
    if (error.status === 409) {
      return { kind: "self-admin", message: error.message };
    }
    if (error.code === "role_set_redundant") {
      return { kind: "redundant", message: error.message };
    }
    if (error.code === "directory_unavailable") {
      return { kind: "directory-unavailable" };
    }
    if (error.isUnauthenticated) {
      return { kind: "unauthenticated" };
    }
    if (error.isForbidden || error.status === 404) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.status >= 500) {
      return {
        kind: "ambiguous",
        code: error.code,
        requestId: error.requestId,
      };
    }
    return {
      kind: "error",
      code: error.code,
      requestId: error.requestId,
      message: error.message,
    };
  }
  return {
    kind: "ambiguous",
    code: "upstream_unavailable",
    requestId: undefined,
  };
}

/** The directory routes' fail-closed answer for a deployment with no directory. */
export function isDirectoryUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.code === "directory_unavailable";
}
